import * as React from "react";
import {
  List,
  useNotify,
  useRefresh,
  useDataProvider,
  TopToolbar,
  FilterButton,
  DateInput,
  useListContext,
  useCreatePath,
  useResourceContext,
} from "react-admin";

import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EventIcon from "@mui/icons-material/Event";
import LinkIcon from "@mui/icons-material/Link";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WorkIcon from "@mui/icons-material/Work";
import { useClub } from "../club/ClubContext";
import { Link as RouterLink } from "react-router-dom";

const MONTH_SHORT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const DAY_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

const OccurrenceFilters = [
  <DateInput key="from_dt" source="from_dt" label="Von" />,
  <DateInput key="to_dt" source="to_dt" label="Bis" />,
];

function OccurrenceActions() {
  const { filterValues, setFilters } = useListContext();
  const includePast = Number(filterValues?.include_past ?? 0);

  return (
    <TopToolbar>
      <FilterButton />
      <Button
        size="small"
        variant={includePast ? "contained" : "outlined"}
        onClick={() => setFilters({ ...filterValues, include_past: includePast ? 0 : 1 }, undefined)}
      >
        {includePast ? "Vergangene: AN" : "Vergangene: AUS"}
      </Button>
    </TopToolbar>
  );
}

function DateBadge({ date, cancelled }: { date: Date; cancelled: boolean }) {
  const theme = useTheme();
  const day = date.getDate();
  const monthIdx = date.getMonth();
  const weekday = DAY_SHORT[date.getDay()];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 52,
        width: 52,
        py: 1,
        borderRadius: 2,
        bgcolor: cancelled
          ? alpha(theme.palette.error.main, 0.1)
          : alpha(theme.palette.primary.main, 0.1),
        border: "1px solid",
        borderColor: cancelled ? "error.main" : "primary.main",
        flexShrink: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontSize: "0.65rem",
          color: cancelled ? "error.main" : "primary.main",
          lineHeight: 1,
          textTransform: "uppercase",
        }}
      >
        {weekday}
      </Typography>
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: "1.5rem",
          lineHeight: 1.1,
          color: cancelled ? "error.main" : "primary.main",
        }}
      >
        {day}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          fontSize: "0.65rem",
          color: cancelled ? "error.main" : "primary.main",
          lineHeight: 1,
          textTransform: "uppercase",
        }}
      >
        {MONTH_SHORT[monthIdx]}
      </Typography>
    </Box>
  );
}

function AttendanceBar({ status }: { status?: string }) {
  if (!status) return null;
  if (status === "YES") return <Chip size="small" label="Zugesagt" color="success" />;
  if (status === "NO") return <Chip size="small" label="Abgesagt" color="error" />;
  return <Chip size="small" label="Vielleicht" color="warning" />;
}

export function EventOccurrenceList(props: any) {
  const { clubId } = useClub();

  return (
    <Box sx={{ width: "100%", maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" }, boxSizing: "border-box", overflowX: "clip" }}>
      <List
        {...props}
        perPage={25}
        filters={OccurrenceFilters}
        actions={<OccurrenceActions />}
        sort={{ field: "starts_at", order: "ASC" }}
        filterDefaultValues={clubId ? { club_id: clubId } : {}}
        filter={clubId ? { club_id: clubId } : {}}
        sx={{
          width: "100%",
          maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" },
          boxSizing: "border-box",
          overflowX: "clip",
          "& .RaList-main": { width: "100%", maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" }, boxSizing: "border-box", overflowX: "clip" },
          "& .RaList-content": { width: "100%", maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" }, boxSizing: "border-box", overflowX: "clip" },
          "& .RaList-actions": { maxWidth: "100%", boxSizing: "border-box", overflowX: "clip" },
        }}
      >
        <OccurrenceCards />
      </List>
    </Box>
  );
}

function OccurrenceCards() {
  const { data, isLoading } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext();
  const createPath = useCreatePath();
  const { clubId } = useClub();
  const theme = useTheme();

  const attCount = (r: any, key: "yes" | "no" | "maybe") => {
    const n = Number(r?.attendance_counts?.[key] ?? 0);
    return n > 0 ? ` (${n})` : "";
  };

  // Wer darf abstimmen: bei team-spezifischen Terminen nur Mitglieder des
  // zugeordneten Teams (Admin-Rollen sind davon ausgenommen).
  const [voteCtx, setVoteCtx] = React.useState<{ teamIds: Set<number>; canVoteAnyTeam: boolean } | null>(null);

  React.useEffect(() => {
    if (!clubId) return;
    (async () => {
      try {
        const { data: json } = await (dataProvider as any).custom(`me?club_id=${clubId}`, { method: "GET" });
        const role = json?.active_membership?.role;
        const canVoteAnyTeam = !!json?.is_superadmin || role === "club_admin" || role === "team_admin";
        const teamIds = new Set<number>((json?.team_ids ?? []).map((x: any) => Number(x)));
        setVoteCtx({ teamIds, canVoteAnyTeam });
      } catch {
        // im Zweifel nicht blockieren – Backend setzt die eigentliche Durchsetzung durch
        setVoteCtx({ teamIds: new Set(), canVoteAnyTeam: true });
      }
    })();
  }, [clubId, dataProvider]);

  const canVote = (r: any) => {
    if (!r.team_ids?.length) return true; // ALL_CLUB
    if (!voteCtx) return true; // noch nicht geladen -> nicht vorschnell blockieren
    if (voteCtx.canVoteAnyTeam) return true;
    return r.team_ids.some((tid: number) => voteCtx.teamIds.has(Number(tid)));
  };

  // Kalender-Abo
  const [calUrl, setCalUrl] = React.useState<string | null>(null);
  const [calLoading, setCalLoading] = React.useState(false);
  const [calError, setCalError] = React.useState<string | null>(null);
  const [calExpanded, setCalExpanded] = React.useState(false);

  const fetchCalendarUrl = React.useCallback(async () => {
    if (!clubId) return;
    try {
      setCalLoading(true);
      setCalError(null);
      const res = await (dataProvider as any).custom(`me/calendar-token?club_id=${clubId}`, { method: "POST" });
      const url = res?.data?.calendar_url as string | undefined;
      if (!url) throw new Error("calendar_url fehlt");
      setCalUrl(url);
    } catch (e: any) {
      setCalError(e?.message ?? "Kalender-Link konnte nicht geladen werden");
    } finally {
      setCalLoading(false);
    }
  }, [clubId, dataProvider]);

  React.useEffect(() => { fetchCalendarUrl(); }, [fetchCalendarUrl]);

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const toWebcal = (url: string) => isIOS() ? url.replace(/^https?:\/\//i, "webcal://") : url;

  const copyCalUrl = async () => {
    if (!calUrl) return;
    try {
      await navigator.clipboard.writeText(toWebcal(calUrl));
      notify("Kalender-Link kopiert", { type: "info" });
    } catch {
      notify("Kopieren nicht möglich", { type: "warning" });
    }
  };

  const downloadIcs = async (id: number) => {
    try {
      const res = await (dataProvider as any).custom(`events/occurrences/${id}.ics`, {
        method: "GET",
        responseType: "text",
        headers: new Headers({ Accept: "text/calendar" }),
      });
      const icsText = res.data as string;
      if (!icsText) throw new Error("Keine ICS-Daten erhalten");
      const blob = new Blob([icsText], { type: "text/calendar;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `termin-${id}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      notify(e?.message ?? "iCal Download fehlgeschlagen", { type: "warning" });
    }
  };

  const setAttendance = async (id: number, status: "YES" | "NO" | "MAYBE") => {
    try {
      await (dataProvider as any).custom(`events/occurrences/${id}/attendance`, {
        method: "PUT",
        body: { status },
      });
      refresh();
    } catch (e: any) {
      const msg =
        e?.status === 403
          ? "Nur Mitglieder des zugeordneten Teams können hier abstimmen."
          : e?.message ?? "Konnte Antwort nicht speichern";
      notify(msg, { type: "warning" });
    }
  };

  if (isLoading) return null;

  const now = new Date();

  return (
    <Stack spacing={2} sx={{ width: "100%", maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" }, boxSizing: "border-box", p: { xs: 0, sm: 1 }, overflowX: "hidden" }}>
      {/* Kalender-Abo Block */}
      <Card
        sx={{
          width: "100%",
          maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" },
          boxSizing: "border-box",
          borderLeft: "4px solid",
          borderLeftColor: "primary.main",
          cursor: "pointer",
        }}
        onClick={() => { if (!calExpanded) { setCalExpanded(true); } }}
      >
        <CardContent sx={{ pb: "12px !important" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthIcon color="primary" fontSize="small" />
              <Typography fontWeight={700} color="primary">
                Kalender abonnieren
              </Typography>
            </Stack>
            <Button
              size="small"
              onClick={(e) => { e.stopPropagation(); setCalExpanded(v => !v); }}
            >
              {calExpanded ? "Einklappen" : "Anzeigen"}
            </Button>
          </Stack>

          {calExpanded && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Abonniere diesen Kalender, damit Termine automatisch in deiner Kalender-App erscheinen.
              </Typography>

              {calError && <Alert severity="warning" sx={{ mb: 1 }}>{calError}</Alert>}

              {calLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="body2">Wird geladen…</Typography>
                </Stack>
              ) : calUrl ? (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CalendarMonthIcon />}
                      onClick={(e) => { e.stopPropagation(); window.open(toWebcal(calUrl), "_blank", "noopener"); }}
                    >
                      Abonnieren
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={(e) => { e.stopPropagation(); copyCalUrl(); }}
                    >
                      Link kopieren
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); fetchCalendarUrl(); }}
                      disabled={calLoading}
                    >
                      Aktualisieren
                    </Button>
                  </Stack>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    iPhone: Link öffnen → „Kalender abonnieren". Google Kalender: „Per URL hinzufügen" (Web-Version).
                  </Alert>
                </Stack>
              ) : (
                <Alert severity="info">Kalender-Link noch nicht geladen.</Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Event Cards */}
      {(data ?? []).map((r: any) => {
        const dt = new Date(r.starts_at);
        const dtEnd = new Date(r.ends_at);
        const cancelled = r.status === "CANCELLED";
        const isPast = dtEnd < now;
        const isAllClub = !r.team_ids?.length;

        const desc = String(r.description ?? "")
          .replace(/\\n/g, "\n").replace(/\r\n/g, "\n").replace(/\\,/g, ",").trim();
        const loc = String(r.location ?? "")
          .replace(/\\n/g, "\n").replace(/\r\n/g, "\n").replace(/\\,/g, ",").trim();

        const startTime = dt.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
        const endTime = dtEnd.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });

        const accentColor = cancelled
          ? theme.palette.error.main
          : r.my_attendance === "YES"
          ? theme.palette.success.main
          : r.my_attendance === "NO"
          ? theme.palette.error.main
          : theme.palette.divider;

        return (
          <Card
            key={r.id}
            sx={{
              width: "100%",
              maxWidth: { xs: "calc(100vw - 24px)", sm: "100%" },
              boxSizing: "border-box",
              borderLeft: "4px solid",
              borderLeftColor: accentColor,
              opacity: (isPast && !cancelled) ? 0.75 : 1,
              transition: "box-shadow 0.15s",
              "&:hover": { boxShadow: 4 },
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ pb: "8px !important", maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, maxWidth: "100%" }}>
                {/* Date Badge */}
                <DateBadge date={dt} cancelled={cancelled} />

                {/* Main Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: "100%" }}>
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          component={RouterLink}
                          to={createPath({ resource, type: "show", id: r.id })}
                          sx={{ textDecoration: "none", color: "inherit", lineHeight: 1.2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}
                        >
                          {r.title}
                        </Typography>
                        <Tooltip title="Details">
                          <IconButton
                            size="small"
                            component={RouterLink}
                            to={createPath({ resource, type: "show", id: r.id })}
                            sx={{ flexShrink: 0 }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {startTime} – {endTime} Uhr
                        {r.timezone && r.timezone !== "Europe/Vienna" && r.timezone !== "Europe/Berlin"
                          ? ` (${r.timezone})` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, minWidth: 0, maxWidth: "45%" }}>
                      {cancelled && <Chip size="small" label="Abgesagt" color="error" />}
                      {!cancelled && <AttendanceBar status={r.my_attendance} />}
                    </Stack>
                  </Stack>

                  {loc && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                      📍 {loc}
                    </Typography>
                  )}

                  {/* Teams */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      mt: 0.75,
                      minWidth: 0,
                      maxWidth: "100%",
                      flexWrap: "wrap",
                      gap: 0.5,
                      "& .MuiChip-root": { maxWidth: "100%" },
                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  >
                    {isAllClub
                      ? <Chip size="small" label="Alle Teams" variant="outlined" />
                      : (r.team_names ?? []).map((t: string) => (
                          <Chip key={t} size="small" label={t} variant="outlined" />
                        ))
                    }
                  </Stack>

                  {desc && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {desc}
                    </Typography>
                  )}

                  {/* Job assignments preview */}
                  {r.job_assignments && r.job_assignments.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        mt: 0.75,
                        minWidth: 0,
                        maxWidth: "100%",
                        flexWrap: "wrap",
                        gap: 0.5,
                        "& .MuiChip-root": { maxWidth: "100%" },
                        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                      }}
                    >
                      <WorkIcon fontSize="inherit" sx={{ color: "text.secondary", mt: "2px" }} />
                      {r.job_assignments.slice(0, 3).map((j: any) => (
                        <Chip
                          key={j.id}
                          size="small"
                          label={`${j.job_type_name}: ${j.player_display_name}`}
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      ))}
                      {r.job_assignments.length > 3 && (
                        <Chip size="small" label={`+${r.job_assignments.length - 3}`} variant="outlined" sx={{ fontSize: "0.7rem" }} />
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>

              {/* Action Bar */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ mt: 1.25, pt: 1, minWidth: 0, maxWidth: "100%", borderTop: "1px solid", borderTopColor: "divider", flexWrap: "wrap", gap: 0.5 }}
              >
                {(() => {
                  const voteBlocked = !cancelled && !canVote(r);
                  const voteTooltip = voteBlocked
                    ? "Nur Mitglieder des zugeordneten Teams können hier abstimmen"
                    : "";
                  return (
                    <>
                      <Tooltip title={voteTooltip}>
                        <span>
                          <Button
                            size="small"
                            variant={r.my_attendance === "YES" ? "contained" : "outlined"}
                            color="success"
                            startIcon={<ThumbUpAltIcon />}
                            disabled={cancelled || voteBlocked}
                            onClick={() => setAttendance(r.id, "YES")}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            Zusagen{attCount(r, "yes")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={voteTooltip}>
                        <span>
                          <Button
                            size="small"
                            variant={r.my_attendance === "NO" ? "contained" : "outlined"}
                            color="error"
                            startIcon={<ThumbDownAltIcon />}
                            disabled={cancelled || voteBlocked}
                            onClick={() => setAttendance(r.id, "NO")}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            Absagen{attCount(r, "no")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={voteTooltip}>
                        <span>
                          <Button
                            size="small"
                            variant={r.my_attendance === "MAYBE" ? "contained" : "outlined"}
                            color="warning"
                            startIcon={<HelpOutlineIcon />}
                            disabled={cancelled || voteBlocked}
                            onClick={() => setAttendance(r.id, "MAYBE")}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            Vielleicht{attCount(r, "maybe")}
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  );
                })()}

                {r.link_url && (
                  <Tooltip title="Externer Link">
                    <IconButton size="small" href={r.link_url} target="_blank" rel="noreferrer">
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="iCal herunterladen">
                  <IconButton size="small" onClick={() => downloadIcs(r.id)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {(data ?? []).length === 0 && (
        <Box textAlign="center" py={6}>
          <EventIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">Keine Termine vorhanden.</Typography>
        </Box>
      )}
    </Stack>
  );
}
